import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, Globe } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { formatPrice } from '../lib/utils';

export function Header() {
  const { cart, cartCount, removeFromCart, updateQuantity, isCartOpen, setIsCartOpen } = useCart();
  const { language, toggleLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: '/folgo', label: 'FOLGO' },
    { href: '/cargo', label: 'CARGO' },
    { href: '/kit-cadre', label: t('nav.kitCadre') },
    { href: '/accessoires', label: t('nav.accessoires') },
    { href: '/a-propos', label: language === 'fr' ? 'À PROPOS' : 'ABOUT' },
    { href: '/faq', label: 'FAQ' },
    { href: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-border">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_belleville-bikes/artifacts/w09oefdr_Capture%20d%E2%80%99e%CC%81cran%202026-03-03%20a%CC%80%2010.13.06.png" 
              alt="BM" 
              className="h-10 md:h-12 w-auto"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_belleville-bikes/artifacts/5f2kp56d_Capture%20d%E2%80%99e%CC%81cran%202026-03-03%20a%CC%80%2010.13.45.png" 
              alt="Belleville Machine" 
              className="h-8 md:h-10 w-auto hidden sm:block"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`font-mono text-sm uppercase tracking-widest transition-colors hover:text-[#0047AB] ${
                  isActive(link.href) ? 'text-[#0047AB]' : 'text-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 p-2 hover:bg-secondary transition-colors font-mono text-xs uppercase"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'fr' ? 'EN' : 'FR'}</span>
            </button>

            <Link
              to="/admin"
              className="hidden md:flex items-center gap-2 font-mono text-xs uppercase tracking-widest hover:text-[#0047AB] transition-colors"
              data-testid="admin-link"
            >
              <User className="w-4 h-4" />
            </Link>

            {/* Cart */}
            <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
              <SheetTrigger asChild>
                <button
                  className="relative p-2 hover:bg-secondary transition-colors"
                  data-testid="cart-button"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-black text-white text-[10px] font-mono w-5 h-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg">
                <SheetHeader>
                  <SheetTitle className="font-heading font-bold uppercase tracking-tight">
                    {t('cart.title')} ({cartCount})
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    {language === 'fr' ? 'Contenu de votre panier' : 'Your shopping cart contents'}
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-8 flex flex-col h-[calc(100vh-240px)]">
                  {cart.items.length === 0 ? (
                    <p className="text-muted-foreground font-mono text-sm">{t('cart.empty')}</p>
                  ) : (
                    <>
                      <div className="flex-1 overflow-auto space-y-4">
                        {cart.items.map((item, index) => (
                          <div key={`${item.product_id}-${index}`} className="border border-border p-4" data-testid={`cart-item-${index}`}>
                            <div className="flex justify-between items-start mb-3">
                              <h4 className="font-heading font-bold text-sm uppercase">{item.product_name}</h4>
                              <span className="font-mono font-bold text-lg">{formatPrice(item.total_price)}</span>
                            </div>
                            
                            {/* Configuration details */}
                            {item.config && (
                              <div className="bg-secondary p-3 mb-3 space-y-1">
                                {item.config.pneu && (
                                  <div className="flex justify-between text-xs">
                                    <span className="font-mono text-muted-foreground">{language === 'fr' ? 'Pneus' : 'Tires'}</span>
                                    <span className="font-mono font-medium uppercase">{item.config.pneu === 'marathon' ? 'Marathon Plus' : 'Gravel'}</span>
                                  </div>
                                )}
                                {item.config.transmission && (
                                  <div className="flex justify-between text-xs">
                                    <span className="font-mono text-muted-foreground">{language === 'fr' ? 'Transmission' : 'Drivetrain'}</span>
                                    <span className="font-mono font-medium">
                                      {item.config.transmission === '1x10' ? '1x10' : item.config.transmission === '1x12' ? '1x12' : 'GRX'}
                                      {item.config.transmission === '1x12' && <span className="text-[#0047AB]"> (+50€)</span>}
                                      {item.config.transmission === 'grx' && <span className="text-[#0047AB]"> (+400€)</span>}
                                    </span>
                                  </div>
                                )}
                                {item.config.guidon && (
                                  <div className="flex justify-between text-xs">
                                    <span className="font-mono text-muted-foreground">{language === 'fr' ? 'Guidon' : 'Handlebar'}</span>
                                    <span className="font-mono font-medium uppercase">{item.config.guidon === 'vtt' ? 'VTT' : 'Gravel'}</span>
                                  </div>
                                )}
                                {item.config.couleur && (
                                  <div className="flex justify-between text-xs">
                                    <span className="font-mono text-muted-foreground">{language === 'fr' ? 'Couleur' : 'Color'}</span>
                                    <span className="font-mono font-medium uppercase">
                                      {item.config.couleur}
                                      {item.config.couleurCustom && <span className="text-[#0047AB]"> (+50€)</span>}
                                    </span>
                                  </div>
                                )}
                                {item.config.taille && (
                                  <div className="flex justify-between text-xs">
                                    <span className="font-mono text-muted-foreground">{language === 'fr' ? 'Taille' : 'Size'}</span>
                                    <span className="font-mono font-medium">{item.config.taille}</span>
                                  </div>
                                )}
                                {item.config.axe && (
                                  <div className="flex justify-between text-xs">
                                    <span className="font-mono text-muted-foreground">{language === 'fr' ? 'Axe' : 'Axle'}</span>
                                    <span className="font-mono font-medium">{item.config.axe === 'QR' ? 'QR 135/10' : 'Traversant 142/12'}</span>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => updateQuantity(index, item.quantity - 1)}
                                  className="w-8 h-8 border border-border flex items-center justify-center hover:bg-secondary transition-colors"
                                  data-testid={`decrease-qty-${index}`}
                                >
                                  -
                                </button>
                                <span className="font-mono w-8 text-center">{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(index, item.quantity + 1)}
                                  className="w-8 h-8 border border-border flex items-center justify-center hover:bg-secondary transition-colors"
                                  data-testid={`increase-qty-${index}`}
                                >
                                  +
                                </button>
                              </div>
                              <button
                                onClick={() => removeFromCart(index)}
                                className="text-xs font-mono uppercase text-muted-foreground hover:text-destructive transition-colors"
                                data-testid={`remove-item-${index}`}
                              >
                                {t('cart.remove')}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-border pt-4 mt-4">
                        <div className="flex justify-between items-center mb-4">
                          <span className="font-heading font-bold uppercase">{t('cart.total')}</span>
                          <span className="font-mono text-2xl font-bold">{formatPrice(cart.total)}</span>
                        </div>
                        <Link to="/checkout" onClick={() => setIsCartOpen(false)}>
                          <Button className="w-full h-12 rounded-none font-mono uppercase tracking-widest" data-testid="checkout-button">
                            {t('cart.checkout')}
                          </Button>
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>

            {/* Mobile Menu */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden border-t border-border py-4 animate-fade-in-up">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`block py-3 font-mono text-sm uppercase tracking-widest transition-colors hover:text-[#0047AB] ${
                  isActive(link.href) ? 'text-[#0047AB]' : 'text-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/admin"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-3 font-mono text-sm uppercase tracking-widest text-muted-foreground hover:text-[#0047AB] transition-colors"
            >
              Admin
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}
